import React from 'react';
import { motion } from 'framer-motion';
import { Zap, FileText, ArrowRight, Gauge, Cpu, Shield, Clock } from 'lucide-react';
import { useLandingContent } from '../../context/LandingContentContext';

const ServicesPromo = () => {
  const lc = useLandingContent();
  const sCaption = lc.services_caption || 'Unsere Services';
  const sTitle = lc.services_title || 'Mehr als nur Teile';
  const s1Title = lc.service_1_title || 'Chiptuning & Software';
  const s1Text = lc.service_1_text || 'Professionelle Softwareoptimierung für über 1.000 Fahrzeugmodelle. Mehr Leistung, besseres Ansprechverhalten und optimierter Verbrauch.';
  const s1Link = lc.service_1_link || 'https://chiptuning.trxl-performance.de/';
  const s1Cta = lc.service_1_cta || 'Chiptuning entdecken';
  const s2Title = lc.service_2_title || 'Fileservice Portal';
  const s2Text = lc.service_2_text || 'Professionelle Tuning-Files für Werkstätten und Tuner weltweit. DPF, EGR, SCR, AdBlue-Lösungen und individuelle Kennfeldoptimierungen.';
  const s2Link = lc.service_2_link || 'https://fileservice.trxl-performance.de/';
  const s2Cta = lc.service_2_cta || 'Fileservice entdecken';
  const ctaTitle = lc.cta_title || 'Du willst mit Chiptuning anfangen?';
  const ctaText = lc.cta_text || 'Kostenlose Beratung per WhatsApp oder Telefon. Wir finden das perfekte Setup!';
  const ctaButton = lc.cta_button || 'WhatsApp Beratung';
  const ctaLink = lc.cta_link || 'https://wa.me/491725498156?text=Hey%20Sven%2C%20ich%20möchte%20mit%20Chiptuning%20anfangen!';

  return (
  <section id="services" className="section-lg" data-testid="services-section">
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "0 1.5rem" }}>
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <div className="caption" style={{ marginBottom: '1rem' }}>{sCaption}</div>
        <h2 className="heading-section" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}>
          Mehr als nur <span style={{ color: 'var(--primary)' }}>Teile</span>
        </h2>
      </motion.div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
        {/* Chiptuning */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          style={{
            position: 'relative', overflow: 'hidden', minHeight: '520px',
            border: '1px solid rgba(255,46,46,0.15)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          }}>
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: 'url(/images/IMG_8433.JPG)',
            backgroundSize: 'cover', backgroundPosition: 'center', filter: 'brightness(0.3) contrast(1.15)',
          }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(255,46,46,0.1), transparent 60%)' }} />
          <div style={{ position: 'relative', padding: '2.5rem' }}>
            <div style={{ width: '48px', height: '48px', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
              <Zap size={24} />
            </div>
            <h3 className="heading-section" style={{ fontSize: '1.75rem', marginBottom: '1rem' }}>{s1Title}</h3>
            <p style={{ color: 'var(--fg-muted)', lineHeight: 1.7, marginBottom: '1rem', fontSize: '0.95rem' }}>
              {s1Text}
            </p>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', fontSize: '0.8rem', color: 'var(--fg-muted)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Gauge size={14} color="var(--primary)" /> Bis +40% Leistung</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Cpu size={14} color="var(--primary)" /> Prüfstand inkl.</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Shield size={14} color="var(--primary)" /> Motorgarantie</span>
            </div>
            <a href={s1Link} target="_blank" rel="noopener noreferrer"
              className="btn-primary" data-testid="service-chiptuning-btn" style={{ display: 'inline-flex' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>{s1Cta} <ArrowRight size={16} /></span>
            </a>
          </div>
        </motion.div>

        {/* Fileservice */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          style={{
            position: 'relative', overflow: 'hidden', minHeight: '520px',
            border: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          }}>
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: 'url(/images/tcr.jpg)',
            backgroundSize: 'cover', backgroundPosition: 'center', filter: 'brightness(0.25) contrast(1.1)',
          }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(255,46,46,0.06), transparent 60%)' }} />
          <div style={{ position: 'relative', padding: '2.5rem' }}>
            <div style={{ width: '48px', height: '48px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
              <FileText size={24} />
            </div>
            <h3 className="heading-section" style={{ fontSize: '1.75rem', marginBottom: '1rem' }}>{s2Title}</h3>
            <p style={{ color: 'var(--fg-muted)', lineHeight: 1.7, marginBottom: '1rem', fontSize: '0.95rem' }}>
              {s2Text}
            </p>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', fontSize: '0.8rem', color: 'var(--fg-muted)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><FileText size={14} color="var(--primary)" /> 5.000+ Files</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Clock size={14} color="var(--primary)" /> 24h Lieferung</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Shield size={14} color="var(--primary)" /> Weltweit</span>
            </div>
            <a href={s2Link} target="_blank" rel="noopener noreferrer"
              className="btn-secondary" data-testid="service-fileservice-btn"
              style={{ display: 'inline-flex', borderColor: 'var(--primary)', color: 'var(--primary)' }}>
              {s2Cta} <ArrowRight size={16} />
            </a>
          </div>
        </motion.div>
      </div>

      {/* CTA Banner */}
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        style={{
          marginTop: '2rem', padding: '2rem 2.5rem', border: '1px solid rgba(255,46,46,0.2)',
          background: 'rgba(255,46,46,0.04)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1.5rem',
        }}>
        <div>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1.25rem', textTransform: 'uppercase' }}>
            {ctaTitle}
          </h3>
          <p style={{ color: 'var(--fg-muted)', fontSize: '0.9rem', marginTop: '0.5rem' }}>
            {ctaText}
          </p>
        </div>
        <a href={ctaLink} target="_blank" rel="noopener noreferrer"
          className="btn-primary" data-testid="chiptuning-whatsapp-cta">
          <span>{ctaButton}</span>
        </a>
      </motion.div>
    </div>
  </section>
  );
};

export default ServicesPromo;
